import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { format } from 'date-fns';

import { toast } from 'react-toastify';

import {
  validarDadosAtualizacaoPaginaCatalogo,
  verificarAtualizacaoPaginas,
} from '../../../lib/inputUtils';
import api from '../../../services/api';

import { UploadTemporarioInput } from '../../../components/Form/UploadTemporarioInput';

import Loading from '../../../components/Loading';
import CreatableSelect from '../../../components/Form/Input/CreatableSelect';
import TitleBar from '../../../components/TitleBar';
import { Form } from '../../../components/Form';
import InputViewer from '../../../components/Form/Input/Viewer';
import Switch from '../../../components/Form/Input/Switch';

import {
  Container,
  WrapperCatalogInfo,
  WrapperInputs,
  WrapperInputPaginas,
  WrapperSwitch,
  AtualizacaoContainer,
} from './styles';
import { AppError } from '../../../errors/AppError';

function CatalogoAtualizar() {
  const formRef = useRef(null);
  const location = useLocation();
  const { id } = location.state;

  const [loading, setLoading] = useState(false);
  const [catalogo, setCatalogo] = useState(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const [habilitaInsercao, setHabilitaInsercao] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  const carregaCatalogo = useCallback(() => {
    api.get(`catalogo/${id}`).then((response) => {
      const { arquivo } = response.data;

      /** formata campo fabricante */
      const fmtFabricante = arquivo.fabricante && arquivo.fabricante.descricao;

      /** carrega as informações do catálogo */
      setCatalogo({
        ...response.data,
        fabricante: fmtFabricante,
      });
    });
  }, [id]);

  const carregarUltimaAtualizacao = useCallback(() => {
    api.get(`catalogo/imagem-ultima-atualizacao/${id}`).then((response) => {
      const insercao = response.data?.insercao ?? null;
      const atualizacao = response.data?.atualizacao ?? null;
      const instInc = response.data?.instInc ?? null;
      const instLido = response.data?.instLido ?? null;

      if (insercao || (atualizacao && instInc)) {
        const fmtInstInc = format(new Date(instInc), 'dd/MM/yyyy HH:mm:ss');
        const fmtInstLido = instLido
          ? format(new Date(instLido), 'dd/MM/yyyy HH:mm:ss')
          : null;

        setUltimaAtualizacao({
          instInc: fmtInstInc,
          instLido: fmtInstLido,
          totalAtualizacao: atualizacao.length,
          totalInsercao: insercao.length,
          temInsercao: insercao.length > 0,
          insercao,
          atualizacao,
        });
      }
    });
  }, [id]);

  useEffect(() => {
    carregaCatalogo();
    carregarUltimaAtualizacao();
  }, [location, id, carregaCatalogo, carregarUltimaAtualizacao]);

  const handleFileUpload = useCallback(
    async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileName', catalogo.arquivo.nome);

      try {
        const response = await api.post('uploads-temporario', formData, {
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            );
            setUploadProgress(progress);
          },
        });

        const { path, originalname } = response.data;

        formRef.current.setFieldValue('path', path);
        setPreviewUrl(originalname);
        toast.success('Arquivo carregado com sucesso!');
      } catch (err) {
        setPreviewUrl('Falha no carregamento do arquivo');
        AppError(err, formRef);
      } finally {
        setUploadProgress(null);
      }
    },
    [catalogo, setUploadProgress, setPreviewUrl]
  );

  const handleConfirmar = useCallback(
    async (data) => {
      setLoading(true);
      try {
        if (data.path.length === 0) {
          alert('Necessário carregar o arquivo primeiro');
          return;
        }

        /* Verificar se as páginas para atualizar estão dentro do número de páginas do catálogo atual */
        const atualizacaoPaginas = validarDadosAtualizacaoPaginaCatalogo(
          data.atualizarPaginas
        );

        if (
          atualizacaoPaginas.dadosValidos.length === 0 &&
          atualizacaoPaginas.dadosInvalidos.length >= 0 &&
          !habilitaInsercao
        ) {
          alert(
            'O campo número das páginas (atualizar) aceita apenas número (positivo) e não pode estar vazio'
          );
          return;
        }

        const {
          dentroRangeNumeracaoCatalogo,
          paginasAtualizacaoValidas,
        } = verificarAtualizacaoPaginas(
          atualizacaoPaginas.dadosValidos,
          catalogo.arquivo.numeroPagina
        );

        if (
          !dentroRangeNumeracaoCatalogo &&
          data.atualizarPaginas.length > 0 &&
          !habilitaInsercao
        ) {
          alert(
            'Os números informados das páginas para atualização não possuem neste catálogo'
          );
          return;
        }

        const paginasProcessadas = await api.patch(`catalogo/${id}/pagina`, {
          fileName: catalogo.arquivo.nome,
          atualizarPaginas: paginasAtualizacaoValidas,
          habilitaInsercao,
          totalPaginasAtual: catalogo.arquivo.numeroPagina,
        });

        toast.success('Cards das páginas atualizadas com sucesso');

        alert(`
        Páginas inseridas: ${paginasProcessadas.data.inserirPaginas?.join(', ')}
        Páginas atualizadas: ${paginasProcessadas.data.atualizarPaginas?.join(
          ', '
        )}
        `);
        window.location.reload();
      } catch (err) {
        AppError(err, formRef);
      } finally {
        setLoading(false);
      }
    },
    [catalogo, id, habilitaInsercao]
  );

  const handleHabilitarAtualizacaoPaginaNova = useCallback(() => {
    setHabilitaInsercao(!habilitaInsercao);
  }, [habilitaInsercao]);

  return (
    <Container>
      <TitleBar back />

      <h2>Atualização Catálogo</h2>
      {catalogo && (
        <>
          <WrapperCatalogInfo>
            <Form initialData={catalogo}>
              <Form.Row>
                <InputViewer
                  id="idCatalogo"
                  name="idCatalogo"
                  label="Catálogo"
                />

                <InputViewer id="nome" name="nome" label="Classe Operacional" />
              </Form.Row>
              <Form.Row>
                <InputViewer
                  id="fabricante"
                  name="fabricante"
                  label="Fabricante"
                />

                <InputViewer id="versao" name="versao" label="Versão" />
              </Form.Row>
              <h3>Informações do arquivo</h3>
              <Form.Row>
                <InputViewer
                  id="arquivoUrl"
                  name="arquivo.url"
                  label="Arquivo"
                  type="link"
                />
                <InputViewer
                  id="arquivoNumeroPagina"
                  name="arquivo.numeroPagina"
                  label="Num. Páginas"
                />
              </Form.Row>
            </Form>

            {ultimaAtualizacao && (
              <AtualizacaoContainer>
                <Form initialData={ultimaAtualizacao}>
                  {ultimaAtualizacao.atualizacao.length > 0 && (
                    <Form.Row>
                      <InputViewer
                        id="paginas atualizadas"
                        name="atualizacao"
                        label="Última Atualização"
                        title={ultimaAtualizacao.totalAtualizacao}
                      />
                      <InputViewer
                        id="total páginas atualizadas"
                        name="totalAtualizacao"
                        label="Total Últimas Páginas Atualizadas"
                      />
                    </Form.Row>
                  )}

                  {ultimaAtualizacao.insercao.length > 0 && (
                    <Form.Row>
                      <InputViewer
                        id="paginas atualizadas"
                        name="insercao"
                        label="Última Inserção"
                        title={ultimaAtualizacao.totalInsercao}
                      />
                      <InputViewer
                        id="total páginas inseridas"
                        name="totalInsercao"
                        label="Total Últimas Páginas Inseridas"
                      />
                    </Form.Row>
                  )}

                  {ultimaAtualizacao.instInc && (
                    <Form.Row>
                      <InputViewer
                        id="data última atualização"
                        name="instInc"
                        label="Data Última Atualização"
                      />
                    </Form.Row>
                  )}

                  {ultimaAtualizacao.temInsercao && (
                    <Form.Row>
                      {ultimaAtualizacao.instLido ? (
                        <Form.Row>
                          <InputViewer
                            id="data última ordenação"
                            name="instLido"
                            label="Data Última Ordenação"
                          />
                        </Form.Row>
                      ) : (
                        <Form.Row>
                          <h3>Refazer ordenação</h3>
                        </Form.Row>
                      )}
                    </Form.Row>
                  )}
                </Form>
              </AtualizacaoContainer>
            )}
          </WrapperCatalogInfo>

          <Form
            id="formAtualizarCatalogo"
            ref={formRef}
            onSubmit={handleConfirmar}
            autoComplete="off"
          >
            <WrapperSwitch>
              <Switch
                rigthText="Habilitar inserção de novas páginas"
                onChange={handleHabilitarAtualizacaoPaginaNova}
                disabled={
                  !ultimaAtualizacao?.instLido && ultimaAtualizacao?.temInsercao
                }
              />
            </WrapperSwitch>
            <WrapperInputs>
              <UploadTemporarioInput
                id="path"
                name="path"
                onFileChange={handleFileUpload}
                uploadProgress={uploadProgress}
                previewUrl={previewUrl}
                disabled={
                  !ultimaAtualizacao?.instLido && ultimaAtualizacao?.temInsercao
                }
              />

              <WrapperInputPaginas>
                <CreatableSelect
                  id="atualizarPaginas"
                  name="atualizarPaginas"
                  label="Número das Páginas (Atualizar)"
                  placeholder="Insira o número da página e pressione enter..."
                  isMulti
                  width={400}
                />
              </WrapperInputPaginas>
            </WrapperInputs>

            <Form.Footer>
              <button
                id="btn-submit"
                type="submit"
                form="formAtualizarCatalogo"
                disabled={
                  !ultimaAtualizacao?.instLido && ultimaAtualizacao?.temInsercao
                }
              >
                Executar
              </button>
            </Form.Footer>
          </Form>
        </>
      )}
      <Loading visible={loading} />
    </Container>
  );
}

export { CatalogoAtualizar };
